type Theme = 'light' | 'dark'

export interface TagChipStyle {
  backgroundColor: string
  borderColor: string
  color: string
}

function hexToRgb(hex: string): [number, number, number] {
  const cleaned = hex.replace('#', '')
  const full = cleaned.length === 3
    ? cleaned.split('').map(c => c + c).join('')
    : cleaned
  const num = parseInt(full, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function mixWithWhite(hex: string, whiteRatio: number): string {
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(
    r + (255 - r) * whiteRatio,
    g + (255 - g) * whiteRatio,
    b + (255 - b) * whiteRatio,
  )
}

function mixWithBlack(hex: string, blackRatio: number): string {
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(r * (1 - blackRatio), g * (1 - blackRatio), b * (1 - blackRatio))
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex)
  return (r * 0.299 + g * 0.587 + b * 0.114) / 255
}

/**
 * Stili inline per un chip tag, theme-aware.
 * Light: pastello solido (mix con bianco), bordo più saturo, testo scurito se troppo chiaro.
 * Dark: alpha-hex semi-trasparente (comportamento legacy).
 * active=true: variante più satura per stato "filtro attivo".
 */
export function getTagChipStyles(
  hex: string,
  theme: Theme,
  active: boolean = false,
): TagChipStyle {
  if (theme === 'dark') {
    return {
      backgroundColor: active ? `${hex}66` : `${hex}33`,
      borderColor: active ? hex : `${hex}66`,
      color: hex,
    }
  }
  const textColor = relativeLuminance(hex) > 0.55 ? mixWithBlack(hex, 0.45) : hex
  return {
    backgroundColor: active ? mixWithWhite(hex, 0.65) : mixWithWhite(hex, 0.8),
    borderColor:     active ? mixWithWhite(hex, 0.3)  : mixWithWhite(hex, 0.55),
    color: textColor,
  }
}
